import React, { useState, useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import '../../styles/SelectFileModal.css';
import { useProcessingOverlay } from '../../context/ProcessingOverlayContext';
import { showMessage } from '../../showMessage';
import type { SharingMetadata } from '../../types';
import { pollUploadJob } from '../../utils/uploadJobPolling';

interface SelectFileModalProperties {
  isVisible: boolean;
  onClose: () => void;
  onDateSelect?: (event_id: string, date: string) => void;
  onReportGenerated?: () => void;
}

interface FileData {
  fileId: number | null;
  dateString: string;
}

interface Event {
  event_id: string;
  eventName: string;
  dates: FileData[];
}

interface Boat {
  id: number;
  boat_id: number;
  name: string;
  sharing?: SharingMetadata | null;
}

// API response types
interface ApiGetUserBoatsResponse {
  success: boolean;
  userBoats: Array<Boat & { sharing?: SharingMetadata | null }>;
  message?: string;
}

interface ApiEventFile {
  id: number;
  date: string | null;
  name?: string | null;
  filename?: string | null;
  display_name?: string | null;
  sharing?: SharingMetadata | null;
}

interface ApiEvent {
  event_id?: string | number | null;
  eventName?: string | null;
  files?: ApiEventFile[] | null;
  sharing?: SharingMetadata | null;
}

interface ApiGetEventsFilesResponse {
  success: boolean;
  events: ApiEvent[];
  message?: string;
}

interface ApiGenerateReportResponse {
  success: boolean;
  message?: string;
  jobId?: string;
  processing?: boolean;
}

const BoatList: React.FC<{
  boats: Boat[];
  onSelectBoat: (boat_id: number) => void;
}> = ({ boats, onSelectBoat }) => {
  return (
    <div className="event-list" role="list">
      {boats.map((boat, index) => (
        <button
          key={boat.id || `boat-${index}`}
          type="button"
          className="event-item"
          onClick={() => onSelectBoat(boat.id)}
        >
          {boat.name}
        </button>
      ))}
    </div>
  );
};

const EventList: React.FC<{
  events: Event[];
  onSelectEvent: (event_id: string) => void;
}> = ({ events, onSelectEvent }) => {
  return (
    <div className="event-list" role="list">
      {events.map((eventItem, index) => (
        <button
          key={eventItem.event_id || `event-${index}`}
          type="button"
          className="event-item"
          onClick={() => onSelectEvent(eventItem.event_id)}
        >
          {eventItem.eventName}
        </button>
      ))}
    </div>
  );
};

const DateList: React.FC<{
  dates: FileData[];
  selectedDate: FileData | null;
  onSelectDate: (date: FileData) => void;
}> = ({ dates, selectedDate, onSelectDate }) => (
  <div className="date-list" role="list">
    {dates.map((dateObject, index) => (
      <button
        key={`date-${index}`}
        type="button"
        className={`date-item ${selectedDate?.fileId === dateObject.fileId ? 'selected' : ''}`}
        aria-pressed={selectedDate?.fileId === dateObject.fileId}
        onClick={() => onSelectDate(dateObject)}
      >
        {dateObject.dateString}
      </button>
    ))}
  </div>
);

const GenerateReportButton: React.FC<{
  onClick: () => void;
  isDisabled: boolean;
  isLoading?: boolean;
  loadingText?: string;
}> = ({ onClick, isDisabled, isLoading = false, loadingText = '' }) => {
  const { t } = useTranslation('common');
  return (
    <button
      className={`generate-report-button ${isDisabled ? 'disabled' : ''}`}
      onClick={onClick}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      aria-busy={isLoading}
    >
      {isLoading ? loadingText : t('modals.generateReport')}
    </button>
  );
};

const SelectFileModal: React.FC<SelectFileModalProperties> = ({
  isVisible,
  onClose,
  onDateSelect,
  onReportGenerated,
}) => {
  const { t } = useTranslation('common');
  const { startProcess, updateProcess, endProcess } = useProcessingOverlay();
  const [boats, setBoats] = useState<Boat[]>([]);
  const [selectedBoat, setSelectedBoat] = useState<number | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<FileData | null>(null);
  const modalContainerReference = useRef<HTMLDivElement>(null);
  const API_BASE_URL: string = process.env.REACT_APP_API_BASE_URL ?? '';

  const baseGenerating = t('reports.generating', 'Generating');
  const loadingMessages = [`${baseGenerating}...`, `${baseGenerating}..`, `${baseGenerating}.`];
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

  useEffect(() => {
    if (isLoading) {
      setLoadingMessageIndex(0);
    }
  }, [isLoading]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    if (isLoading) {
      intervalId = setInterval(() => {
        setLoadingMessageIndex((previousIndex) => (previousIndex + 1) % loadingMessages.length);
      }, 500);
    }
    return () => {
      clearInterval(intervalId);
    };
  }, [isLoading, loadingMessages.length]);

  // Fetch user's boats when the modal is visible and none is selected
  useEffect(() => {
    if (isVisible && !selectedBoat) {
      const fetchBoats = async () => {
        try {
          const url = `${API_BASE_URL}/api/user/getUserBoats`;
          const token = localStorage.getItem('token') ?? '';
          const headersBoats: Record<string, string> = {
            'Content-Type': 'application/json',
          };
          if (token) {
            headersBoats.Authorization = `Bearer ${token}`;
          }
          const response = await fetch(url, {
            method: 'POST',
            headers: headersBoats,
            body: JSON.stringify({}),
          });
          const jsonBoats: unknown = await response.json();
          const data = jsonBoats as ApiGetUserBoatsResponse;
          if (data.success) {
            const ownBoats = data.userBoats.filter(
              (boat) => (boat.sharing ? boat.sharing.isOwner : true),
            );
            setBoats(ownBoats);
            setSelectedBoat((previousSelected) =>
              previousSelected !== null && ownBoats.some((boat) => boat.id === previousSelected)
                ? previousSelected
                : null,
            );
          } else {
            console.error('Error fetching boats:', data.message);
          }
        } catch (error) {
          console.error('Error fetching boats:', error);
        }
      };
      void fetchBoats();
    }
  }, [isVisible, selectedBoat, API_BASE_URL]);

  // Fetch events once a boat is selected
  useEffect(() => {
    if (selectedBoat) {
      const fetchEvents = async () => {
        try {
          const url = `${API_BASE_URL}/api/user/getEventsFiles`;
          const token = localStorage.getItem('token') ?? '';
          const headersEvents: Record<string, string> = {
            'Content-Type': 'application/json',
          };
          if (token) {
            headersEvents.Authorization = `Bearer ${token}`;
          }
          const response = await fetch(url, {
            method: 'POST',
            headers: headersEvents,
            body: JSON.stringify({
              filter: 'false',
              user_boat_id: selectedBoat,
            }),
          });

          const jsonEvents: unknown = await response.json();
          const data = jsonEvents as ApiGetEventsFilesResponse;
          if (data.success) {
            const ownEvents = data.events.filter((eventApi: ApiEvent) =>
              eventApi.sharing ? eventApi.sharing.isOwner : true,
            );
            const fetchedEvents: Event[] = ownEvents.map((eventApi: ApiEvent) => {
              const hasEventId =
                typeof eventApi.event_id === 'string' || typeof eventApi.event_id === 'number';
              return {
                event_id: hasEventId ? String(eventApi.event_id) : `event-${Math.random()}`,
                eventName: eventApi.eventName ?? t('modals.nameNotAvailable'),
                dates: Array.isArray(eventApi.files)
                  ? eventApi.files
                      .filter((file) => (file.sharing ? file.sharing.isOwner : true))
                      .map((file: ApiEventFile) => {
                        const dateYmd =
                          typeof file.date === 'string' || typeof file.date === 'number'
                            ? (() => {
                                const d = new Date(file.date);
                                return Number.isNaN(d.getTime()) ? null : d.toISOString().split('T')[0];
                              })()
                            : null;
                        const baseNameCandidate =
                          typeof file.display_name === 'string' && file.display_name.trim().length > 0
                            ? file.display_name.trim()
                            : typeof file.name === 'string' && file.name.trim().length > 0
                              ? file.name.trim()
                              : typeof file.filename === 'string' && file.filename.trim().length > 0
                                ? file.filename.trim()
                                : null;
                        const baseName =
                          (baseNameCandidate && baseNameCandidate.includes('T')
                            ? baseNameCandidate.split('T')[0]
                            : baseNameCandidate) ?? dateYmd;
                        const fallbackDate =
                          dateYmd ??
                          (file.date
                            ? String(file.date)
                            : t('modals.dateNotAvailable', 'Date not available'));
                        return {
                          fileId: file.id ?? null,
                          dateString: baseName ?? fallbackDate,
                        };
                      })
                  : [],
              };
            });
            // Ocultar eventos sin fechas/archivos disponibles
            const filteredEvents = fetchedEvents.filter(
              (eventItem: Event) => Array.isArray(eventItem.dates) && eventItem.dates.length > 0,
            );
            setEvents(filteredEvents);
            setSelectedEvent((previousSelected) =>
              previousSelected &&
              filteredEvents.some((eventItem) => eventItem.event_id === previousSelected)
                ? previousSelected
                : null,
            );
            setSelectedDate((previousSelected) =>
              previousSelected &&
              filteredEvents.some((eventItem) =>
                eventItem.dates.some((date) => date.fileId === previousSelected.fileId),
              )
                ? previousSelected
                : null,
            );
          } else {
            console.error('Error fetching events:', data.message);
          }
        } catch (error) {
          console.error('Error fetching events:', error);
        }
      };
      void fetchEvents();
    }
  }, [selectedBoat, API_BASE_URL]);

  // Scroll to top on view change
  useEffect(() => {
    if (selectedEvent) {
      setTimeout(() => {
        if (modalContainerReference.current) {
          modalContainerReference.current.scrollTop = 0;
        }
      }, 0);
    }
  }, [selectedEvent]);

  const handleGenerateReport = async () => {
    let processId: string | undefined;
    try {
      if (selectedEvent && selectedDate?.fileId) {
        const selectedEventAtStart = selectedEvent;
        const selectedDateAtStart = selectedDate;
        setIsLoading(true);
        // Find event name for user-friendly label
        const eventFound = events.find((eventItem) => eventItem.event_id === selectedEventAtStart);
        const eventName = eventFound?.eventName || t('common.labels.event');
        processId = startProcess('generate', {
          label: 'Generando informe',
          sublabel: `${eventName} · ${selectedDateAtStart.dateString}`,
          progress: 5,
        });
        const url = `${API_BASE_URL}/api/user/GenerateReport`;
        const requestBody = { fileId: selectedDateAtStart.fileId };
        const token = localStorage.getItem('token') ?? '';
        const headersGenerate: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (token) {
          headersGenerate.Authorization = `Bearer ${token}`;
        }
        const response = await fetch(url, {
          method: 'POST',
          headers: headersGenerate,
          body: JSON.stringify(requestBody),
        });
        const jsonGenerate: unknown = await response.json();
        const data = jsonGenerate as ApiGenerateReportResponse;
        setIsLoading(false);
        if (data.success || data.jobId) {
          if (!data.jobId || !token) {
            throw new Error('The server did not return a report job id');
          }
          await pollUploadJob({
            baseUrl: API_BASE_URL,
            token,
            jobId: data.jobId,
            onUpdate: (job) => {
              if (processId) {
                updateProcess(processId, {
                  label: job.message || 'Generando informe',
                  sublabel: `${eventName} · ${selectedDateAtStart.dateString}`,
                  progress: job.progress,
                });
              }
            },
          });
          setEvents((previousEvents) =>
            previousEvents.map((event) => {
              if (event.event_id === selectedEventAtStart) {
                return {
                  ...event,
                  dates: event.dates.filter(
                    (dateObject) => dateObject.fileId !== selectedDateAtStart.fileId,
                  ),
                };
              }
              return event;
            }),
          );
          showMessage('success', t('notifications.success'), t('reports.generatedSuccess'));
          // Notify ReportsPage (if mounted) to refresh list
          window.dispatchEvent(new CustomEvent('reports:refresh'));
          setSelectedDate((previous) =>
            previous && previous.fileId === selectedDateAtStart.fileId ? null : previous,
          );
          if (onDateSelect) {
            onDateSelect(selectedEventAtStart, selectedDateAtStart.dateString);
          }
          if (onReportGenerated) {
            onReportGenerated();
          }
        } else {
          console.error('Error generating report:', data.message);
        }
        if (processId) {
          endProcess(processId);
        }
      } else {
        console.warn('No valid event or date selected');
      }
    } catch (error) {
      console.error('Error generating the report:', error);
      setIsLoading(false);
      if (processId) {
        endProcess(processId);
      }
    }
  };

  const handleSelectBoat = (boat_id: number) => {
    setSelectedBoat(boat_id);
  };

  const handleSelectEvent = (event_id: string) => {
    setSelectedEvent(event_id);
    setSelectedDate(null);
  };

  const handleSelectDate = (dateObject: FileData) => {
    setSelectedDate(dateObject);
  };

  const handleBackClick = () => {
    if (selectedEvent) {
      setSelectedEvent(null);
      setSelectedDate(null);
    } else if (selectedBoat) {
      setSelectedBoat(null);
      setEvents([]);
    }
  };

  // Close on outside click and Escape key
  useEffect(() => {
    if (!isVisible) {
      return;
    }
    const handleMouseDown = (event: MouseEvent) => {
      const container = modalContainerReference.current;
      if (container && !container.contains(event.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible, onClose]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="modal-overlay">
      <div
        className="modal-container"
        role="dialog"
        aria-modal="true"
        ref={modalContainerReference}
      >
        <div className="modal-header">
          {(selectedBoat || selectedEvent) && (
            <button
              type="button"
              className="icon-button back-button"
              aria-label={t('modals.goBack')}
              onClick={handleBackClick}
            >
              <i className="fas fa-arrow-left" aria-hidden="true" />
            </button>
          )}
          <h2>
            {selectedBoat
              ? selectedBoat && !selectedEvent
                ? t('modals.selectEvent')
                : t('modals.selectDate')
              : t('modals.selectBoat')}
          </h2>
        </div>
        <div className="modal-content">
          {selectedBoat ? (
            selectedEvent ? (
              <DateList
                dates={events.find((event) => event.event_id === selectedEvent)?.dates || []}
                selectedDate={selectedDate}
                onSelectDate={handleSelectDate}
              />
            ) : (
              <EventList events={events} onSelectEvent={handleSelectEvent} />
            )
          ) : (
            <BoatList boats={boats} onSelectBoat={handleSelectBoat} />
          )}
        </div>
        <div className="modal-footer">
          {selectedEvent && (
            <GenerateReportButton
              onClick={handleGenerateReport}
              isDisabled={!selectedDate || !selectedDate.fileId}
              isLoading={isLoading}
              loadingText={loadingMessages[loadingMessageIndex]}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export { SelectFileModal };
